import React from "react";
import PropTypes from "prop-types";
import InfoIcon from "../../components/InfoIcon";
import { IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import TipBox from "../../components/TipBox";
import { useTableVariant } from "./TableVariantContext";

const SubTotalRow = ({ columns, subtotalColumns, data, hiddenColumns = [] }) => {
  const { t } = useTranslation();
  const isLovable = useTableVariant() === "lovable";
  if (!data || data.length === 0) {
    return null;
  }
  
  if (!columns || !Array.isArray(columns)) {
    return null;
  }

  // Filter out any undefined/null rows
  const validData = Array.isArray(data) ? data.filter(row => row != null) : [];

  const calculateSubtotal = (accessor) => {
    if (!accessor) return 0;
    return validData.reduce((sum, row) => {
      if (!row || typeof row !== 'object') return sum;
      const value = row[accessor];
      return sum + (parseFloat(value) || 0);
    }, 0);
  };

  const hasNullValues = (accessor) => {
    if (!accessor) return false;
    return validData.some((row) => {
      if (!row || typeof row !== 'object') return false;
      const value = row[accessor];
      return value === null || value === "";
    });
  };

  if (isLovable) {
    const subtotalAccessor = columns.find(
      (col) =>
        col?.accessor &&
        subtotalColumns?.[col.accessor] &&
        !hiddenColumns.includes(col.accessor)
    )?.accessor;

    const totalValue = subtotalAccessor
      ? (() => {
          const col = columns.find((c) => c.accessor === subtotalAccessor);
          const raw = calculateSubtotal(subtotalAccessor);
          return col?.subtotalFixed
            ? `${raw.toFixed(2)} ${col.subtotalUnit || ""}`.trim()
            : `${raw} ${col?.subtotalUnit || ""}`.trim();
        })()
      : "0.00";

    const labelColSpan = Math.max(
      columns.filter(
        (c) =>
          c?.accessor &&
          !hiddenColumns.includes(c.accessor) &&
          c.accessor !== subtotalAccessor
      ).length,
      1
    );

    return (
      <tfoot>
        <tr className="subtotal-row subtotal-row--lovable">
          <td colSpan={labelColSpan} className="lovable-tfoot-label">
            Total:
          </td>
          {subtotalAccessor && (
            <td className="lovable-tfoot-total">
              <div className="lovable-total-box">
                <span>{totalValue}</span>
              </div>
            </td>
          )}
          <td />
        </tr>
      </tfoot>
    );
  }

  return (
    <tfoot>
      <tr className="subtotal-row">
        {columns.map((column, index) => {
          if (!column || !column.accessor) return <td key={`empty-${index}`}></td>;
          const accessor = column.accessor;
          const isHidden = Array.isArray(hiddenColumns) && hiddenColumns.includes(accessor);
          return (
            <td
              className="subtotal-exists"
              key={accessor || `column-${index}`}
              style={{
                width: isHidden ? "30px" : "auto",
                padding: isHidden ? "0 5px" : "8px",
                textAlign: "center",
                position: "relative",
              }}
            >
              {!isHidden &&
                subtotalColumns &&
                subtotalColumns[accessor] && (
                <>
                  <span className="subtotal-span">
                    {column.subtotalFixed
                      ? calculateSubtotal(accessor).toFixed(2) +
                        " " +
                        (column.subtotalUnit || "")
                      : calculateSubtotal(accessor) +
                        " " +
                        (column.subtotalUnit || "")}
                  </span>
                  {hasNullValues(accessor) && (
                    <TipBox
                      title={"Tip"}
                      customStyle={{ margin: "0 auto" }}
                      message={t("TIP_NO_MONETARY")}
                    >
                      <div
                        style={{
                          position: "absolute",
                          bottom: "-13px",
                          left: "40%",
                        }}
                      >
                        <IconButton size="small">
                          <InfoIcon />
                        </IconButton>
                      </div>
                    </TipBox>
                  )}
                </>
              )}
            </td>
          );
        })}
        <td></td>
      </tr>
    </tfoot>
  );
};

SubTotalRow.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  subtotalColumns: PropTypes.objectOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  hiddenColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default SubTotalRow;
